import React from "react";

export default function JobDetailsApiDoc() {
  return (
    <div className="text-gray-800 text-md leading-7 px-4 min-w-0 max-w-[40rem] lg:mt-0 lg:mr-0 lg:max-w-[50rem]">
      {/* Header */}
      <header className="relative overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
        <div className="absolute inset-0 bg-gradient-to-br from-sky-50 via-white to-transparent" />
        <div className="relative p-8 sm:p-12">
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">
            Jobs API — Job Details
          </h1>
          <p className="mt-3 max-w-3xl text-base sm:text-lg leading-7 text-slate-600">
            Retrieve a single job with services, retainers, attachments, tags,
            and computed fields. This endpoint requires authentication and
            applies <span className="font-semibold">role-based visibility</span>{" "}
            (services and price may be restricted based on the user).
          </p>

          {/* Quick facts */}
          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="text-xs uppercase tracking-wide text-slate-500">
                Method
              </div>
              <div className="mt-1 font-mono text-sm text-slate-900">GET</div>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="text-xs uppercase tracking-wide text-slate-500">
                Auth
              </div>
              <div className="mt-1 font-mono text-sm text-slate-900">JWT</div>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="text-xs uppercase tracking-wide text-slate-500">
                Endpoint
              </div>
              <div className="mt-1 font-mono text-sm text-slate-900">
                /api/jobs/{`{id}`}/
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Body */}
      <main className="mt-10 rounded-3xl border border-slate-200 bg-white p-6 sm:p-10 shadow-sm">
        {/* Overview */}
        <section>
          <h2 className="text-xl font-bold tracking-tight text-slate-900">
            Overview
          </h2>
          <p className="mt-3 leading-7 text-slate-700">
            Returns a detailed representation of a job. The server evaluates
            permissions and applies role-based logic for which services you can
            see and whether job price is returned.
          </p>

          <ul className="mt-3 grid list-disc gap-2 pl-5 text-slate-700 marker:text-slate-400">
            <li>
              Access is restricted via internal rules. If you cannot view the
              job, you receive <code className="font-mono">403</code>.
            </li>
            <li>
              <span className="font-semibold">Service visibility</span> depends
              on role: internal/admin/customer users generally see all services;
              project managers may only see services assigned to them.
            </li>
            <li>
              <span className="font-semibold">Price visibility</span> may be
              restricted: some users will receive{" "}
              <code className="font-mono">price: null</code>.
            </li>
            <li>
              The response includes an{" "}
              <code className="font-mono">is_auto_priced</code> flag indicating
              whether the current job price is automatically calculated or has
              been manually overridden.
            </li>
            <li>
              For auto-priced jobs, the server may recompute and update the job
              price before returning the response.
            </li>
          </ul>
        </section>

        {/* Request */}
        <section className="mt-10">
          <h2 className="text-xl font-bold tracking-tight text-slate-900">
            Request
          </h2>

          <div className="mt-4">
            {/* HTTP */}
            <div className="rounded-2xl border border-slate-200 bg-white p-5">
              <div className="text-sm font-semibold text-slate-900">HTTP</div>
              <ul className="mt-2 space-y-1 text-sm leading-6 text-slate-700">
                <li>
                  <span className="font-semibold">Method:</span>{" "}
                  <code className="font-mono">GET</code>
                </li>
                <li>
                  <span className="font-semibold">URL:</span>{" "}
                  <code className="font-mono">/api/jobs/{`{id}`}/</code>
                </li>
                <li>
                  <span className="font-semibold">Headers:</span>
                  <br />
                  <code className="font-mono">
                    Authorization: JWT your_token
                  </code>
                </li>
              </ul>
            </div>

            {/* Path params */}
            <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-5">
              <div className="text-sm font-semibold text-slate-900">
                Path parameters
              </div>
              <ul className="mt-2 space-y-2 text-sm leading-6 text-slate-700">
                <li>
                  <code className="font-mono">id</code> —{" "}
                  <span className="font-mono">number</span>
                  <span className="ml-2 rounded-full border border-slate-300 px-2 py-0.5 text-xs">
                    required
                  </span>{" "}
                  Job ID to fetch.
                </li>
              </ul>
            </div>
          </div>

          {/* Example: cURL */}
          <div className="mt-6 not-prose my-6 overflow-hidden rounded-2xl bg-zinc-900 shadow-md dark:ring-1 dark:ring-white/10">
            <div className="flex min-h-[calc(theme(spacing.12)+1px)] flex-wrap items-start gap-x-4 border-b border-zinc-700 bg-zinc-800 px-4 dark:border-zinc-800 dark:bg-transparent">
              <h3 className="mr-auto pt-3 text-sm font-semibold text-white relative top-1">
                Request
              </h3>
            </div>
            <div className="flex h-9 items-center gap-2 px-4 border-b border-zinc-700">
              <div className="dark flex">
                <span className="font-mono text-[0.625rem] font-semibold leading-6 text-emerald-500 dark:text-emerald-400">
                  GET
                </span>
              </div>
              <span className="h-0.5 w-0.5 rounded-full bg-zinc-500"></span>
              <span className="font-mono text-xs text-zinc-400">
                /api/jobs/{`{id}`}/
              </span>
            </div>
            <div className="group dark:bg-white/2.5 pb-3">
              <div className="relative">
                <pre className="overflow-x-auto p-4 text-xs text-white mt-2">
                  {`curl -X GET https://api.livetakeoff.com/api/jobs/9876/ \\
  -H "Authorization: JWT YOUR_ACCESS_TOKEN"`}
                </pre>
              </div>
            </div>
          </div>
        </section>

        {/* Job Status Legend */}
        <section className="mt-10">
          <h2 className="text-xl font-bold tracking-tight text-slate-900">
            Job Status Legend
          </h2>

          <p className="mt-3 leading-7 text-slate-700">
            Each job includes a <code className="font-mono">status</code> field
            that represents its current stage in the workflow.
          </p>

          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-2">
            {[
              {
                code: "U",
                label: "Submitted",
                color: "bg-gray-100 text-gray-700 border-gray-200",
              },
              {
                code: "A",
                label: "Received",
                color: "bg-blue-100 text-blue-700 border-blue-200",
              },
              {
                code: "S",
                label: "Assigned",
                color: "bg-indigo-100 text-indigo-700 border-indigo-200",
              },

              {
                code: "W",
                label: "Work in Progress",
                color: "bg-yellow-100 text-yellow-700 border-yellow-200",
              },
              {
                code: "C",
                label: "Complete",
                color: "bg-green-100 text-green-700 border-green-200",
              },
              {
                code: "T",
                label: "Cancelled",
                color: "bg-red-100 text-red-700 border-red-200",
              },
              {
                code: "R",
                label: "Review",
                color: "bg-purple-100 text-purple-700 border-purple-200",
              },
              {
                code: "I",
                label: "Invoiced",
                color: "bg-emerald-100 text-emerald-700 border-emerald-200",
              },
              {
                code: "N",
                label: "Not Invoiced",
                color: "bg-orange-100 text-orange-700 border-orange-200",
              },
            ].map((status) => (
              <div
                key={status.code}
                className="flex items-center justify-between rounded-xl border bg-white px-4 py-3 shadow-sm"
              >
                <div className="text-sm text-slate-700">
                  <span className="font-semibold">{status.label}</span>
                </div>

                <div
                  className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-semibold ${status.color}`}
                >
                  <span className="font-mono">{status.code}</span>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Response */}
        <section className="mt-10">
          <h2 className="text-xl font-bold tracking-tight text-slate-900">
            Response
          </h2>

          <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-5">
            <div className="text-sm font-semibold text-slate-900">
              200 OK (shape overview)
            </div>
            <p className="mt-2 text-sm leading-6 text-slate-700">
              The response is returned by{" "}
              <code className="font-mono">JobDetailSerializer</code>, but the
              server injects several computed fields first:
            </p>

            <ul className="mt-3 grid list-disc gap-2 pl-5 text-slate-700 marker:text-slate-400">
              <li>
                <code className="font-mono">special_instructions</code> — pulled
                from customer settings (if available).
              </li>
              <li>
                <code className="font-mono">service_assignments</code> — array
                of service assignments (role-scoped).
              </li>
              <li>
                <code className="font-mono">retainer_service_assignments</code>{" "}
                — array of retainer service assignments (role-scoped).
              </li>
              <li>
                <code className="font-mono">total_photos</code> — count of job
                photos.
              </li>
              <li>
                <code className="font-mono">encoded_id</code> — encoding to
                represent this job.
              </li>
              <li>
                <code className="font-mono">price</code> — may be{" "}
                <code className="font-mono">null</code> depending on user role /
                settings.
              </li>
              <li>
                <code className="font-mono">is_auto_priced</code> — indicates
                whether the value in <code className="font-mono">price</code> is
                automatically calculated (<code>true</code>) or has been
                manually overridden (<code>false</code>).
              </li>
            </ul>

            {/* Example response (minimal) */}
            <div className="mt-4 not-prose my-6 overflow-hidden rounded-2xl bg-zinc-900 shadow-md dark:ring-1 dark:ring-white/10">
              <div className="flex min-h-[calc(theme(spacing.12)+1px)] flex-wrap items-start gap-x-4 border-b border-zinc-700 bg-zinc-800 px-4 dark:border-zinc-800 dark:bg-transparent">
                <h3 className="mr-auto pt-3 text-sm font-semibold text-white relative top-1">
                  Response
                </h3>
              </div>
              <div className="flex h-9 items-center gap-2 px-4 border-b border-zinc-700">
                <div className="dark flex">
                  <span className="font-mono text-[0.625rem] font-semibold leading-6 text-emerald-500 dark:text-emerald-400">
                    JSON
                  </span>
                </div>
                <span className="h-0.5 w-0.5 rounded-full bg-zinc-500"></span>
                <span className="font-mono text-xs text-zinc-400">
                  application/json
                </span>
              </div>
              <div className="group dark:bg-white/2.5 pb-3">
                <div className="relative">
                  <pre className="overflow-x-auto p-4 text-xs text-white mt-2">
                    {`{
  "id": 9876,
  "tailNumber": "N123AB",
  "status": "W",
  "customer": { "id": 42, "name": "Customer Name" },
  "airport": { "id": 15, "name": "KFXE" },
  "fbo": { "id": 3, "name": "Signature" },

  "special_instructions": "Gate code ...",
  "total_photos": 4,
  "encoded_id": "OTg3Ni1OMTIzQUI=",

  "price": 1250.00,

  "is_auto_priced": true,

  "service_assignments": [
    {
      "id": 111,
      "name": "Basic Exterior",
      "short_name": "Exterior L1",
      "short_description": "Quick wash",
      "description": "Full description...",
      "project_manager": "pm_username",
      "is_exterior_wet_wash": false,
      "status": "W",
      "checklist_actions": []
    }
  ],
  "retainer_service_assignments": []
}`}
                  </pre>
                </div>
              </div>
            </div>
          </div>

          {/* Errors */}
          <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-5">
            <div className="text-sm font-semibold text-slate-900">Errors</div>
            <ul className="mt-2 space-y-1 text-sm leading-6 text-slate-700">
              <li>
                <span className="font-mono">403</span> — you do not have
                permission to view this job.
              </li>
              <li>
                <span className="font-mono">404</span> — job not found.
              </li>
            </ul>

            <div className="mt-4 not-prose my-6 overflow-hidden rounded-2xl bg-zinc-900 shadow-md dark:ring-1 dark:ring-white/10">
              <div className="flex min-h-[calc(theme(spacing.12)+1px)] flex-wrap items-start gap-x-4 border-b border-zinc-700 bg-zinc-800 px-4 dark:border-zinc-800 dark:bg-transparent">
                <h3 className="mr-auto pt-3 text-sm font-semibold text-white relative top-1">
                  Response
                </h3>
              </div>
              <div className="flex h-9 items-center gap-2 px-4 border-b border-zinc-700">
                <div className="dark flex">
                  <span className="font-mono text-[0.625rem] font-semibold leading-6 text-emerald-500 dark:text-emerald-400">
                    JSON
                  </span>
                </div>
                <span className="h-0.5 w-0.5 rounded-full bg-zinc-500"></span>
                <span className="font-mono text-xs text-zinc-400">
                  application/json
                </span>
              </div>
              <div className="group dark:bg-white/2.5 pb-3">
                <div className="relative">
                  <pre className="overflow-x-auto p-4 text-xs text-white mt-2">
                    {`{
  "error": "You do not have permission to view this job"
}`}
                  </pre>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Permissions & rules */}
        <section className="mt-10">
          <h2 className="text-xl font-bold tracking-tight text-slate-900">
            Permissions & rules
          </h2>

          <p className="mt-3 leading-7 text-slate-700">
            The server uses internal rules to determine whether a user can view
            the job, and what data is visible in the response.
          </p>

          <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-5">
            <div className="text-sm font-semibold text-slate-900">
              Who can view the job?
            </div>
            <ul className="mt-2 grid list-disc gap-2 pl-5 text-slate-700 marker:text-slate-400">
              <li>Superuser / staff users.</li>
              <li>
                Users in <span className="font-semibold">Account Managers</span>{" "}
                or <span className="font-semibold">Internal Coordinators</span>.
              </li>
              <li>
                Customer users matching the job customer (including extra
                customers via user-customer mapping).
              </li>
              <li>
                Project managers assigned to at least one service or retainer on
                this job.
              </li>
              <li>
                Vendor master PMs may view jobs that have at least one service
                assigned to their vendor.
              </li>
              {/* <li>
                <span className="font-semibold">Portside Integration</span>{" "}
                group users may view jobs only when job origin matches Portside.
              </li> */}
            </ul>
          </div>

          <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-5">
            <div className="text-sm font-semibold text-slate-900">
              Price visibility rules
            </div>
            <ul className="mt-2 grid list-disc gap-2 pl-5 text-slate-700 marker:text-slate-400">
              <li>
                Customer users may see price only if customer settings allow
                price and the customer user profile enables it.
              </li>
              <li>
                Project managers (who are not internal coordinators) do not
                receive job price (price is returned as{" "}
                <code className="font-mono">null</code>).
              </li>
            </ul>
          </div>

          <div className="mt-6 rounded-2xl border border-amber-200 bg-amber-50 p-5">
            <div className="text-sm font-semibold text-amber-900">
              Important note (pricing synchronization)
            </div>

            <div className="mt-3 space-y-3 text-sm leading-6 text-amber-900">
              <p>
                This endpoint returns both the current job{" "}
                <code className="font-mono">price</code> and the{" "}
                <code className="font-mono">is_auto_priced</code> flag.
              </p>

              <p>
                When <code className="font-mono">is_auto_priced</code> is{" "}
                <code>true</code>, the job price is automatically calculated by
                LiveTakeoff. The server may recompute the price before returning
                the response, and consumers may retrieve the detailed pricing
                using the{" "}
                <span className="font-semibold">Job Price Breakdown</span>{" "}
                endpoint.
              </p>

              <p>
                When <code className="font-mono">is_auto_priced</code> is{" "}
                <code>false</code>, the job price has been manually overridden
                by a LiveTakeoff user. In this case, the{" "}
                <span className="font-semibold">Job Details</span> endpoint
                becomes the authoritative source for the current price, and the
                Job Price Breakdown endpoint should no longer be used, since it
                continues to represent the original automatically calculated
                pricing.
              </p>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}
